import { useContext, useEffect, useState, useCallback } from "react";
import { Chart } from "react-google-charts";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { useResizeDetector } from "react-resize-detector";
import DashboardService from "../../../../services/DashboardService";
import AuthContext from "../../../../auth/AuthContext";
import { Skeleton } from "primereact";

const statusColors = {
  "In Progress": "#7DC9FF",
   Pending: "#157ABE",
    Closed: "#f4511e",
};

const AssessmentBarChart = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const authContext = useContext(AuthContext);

  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState([["Assessment"]]);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const shortenName = useCallback((name, maxLength = 100) => {
    if (!name) return "";
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + "...";
  }, []);

  const formatChartData = useCallback(
    (data, status) => {
      const keyMap = {
        "In Progress": "inProgress",
         Pending: "pending",
         Closed: "closed",
      };
      const allStatuses = ["In Progress","Pending","Closed"];

      if (status !== "All") {
        return [
          ["Assessment", status],
          ...data.map((item) => [
            { v: shortenName(item.assessmentName || ""), f: item.assessmentName || "" },
            item[keyMap[status]] || 0,
          ]),
        ];
      }

      const activeStatuses = allStatuses.filter((statusName) =>
        data.some((item) => (item[keyMap[statusName]] || 0) > 0)
      );

      if (activeStatuses.length === 0) {
        return [["Assessment"], ["No data", 0]];
      }

      const header = ["Assessment", ...activeStatuses];
      const rows = data.map((item) => {
        const row = [{ v: shortenName(item.assessmentName || ""), f: item.assessmentName || "" }];
        activeStatuses.forEach((s) => {
          row.push(item[keyMap[s]] || 0);
        });
        return row;
      });

      return [header, ...rows];
    },
    [shortenName]
  );

useEffect(() => {
    const controller = new AbortController();

    const fetchAssessmentData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await DashboardService.GetAssessmentChart(
          authContext,
          controller.signal
        );

        if (controller.signal.aborted) return; 

        if (!response) return;
            if (response?.isSuccess) {
  setRawData(Array.isArray(response.data) ? response.data : []);
} else {
          setError("Failed to fetch assessments: " + (response?.message || "No data"));
        }
      } catch (err) {
        if (err.name === "AbortError") return; 
        setError("Failed to fetch assessments: " + err.message);
        console.error("Assessment chart API error:", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false); 
      }
    };

    fetchAssessmentData();

    return () => controller.abort();
  }, [authContext]);

  useEffect(() => {
    if (rawData.length > 0) {
      setChartData(formatChartData(rawData, selectedStatus));
    } else {
      setChartData([["Assessment"], ["No data", 0]]);
    }
  }, [rawData, selectedStatus, formatChartData]);

  // Dynamic colors 
  const getActiveColors = useCallback(() => {
    if (selectedStatus !== "All") {
      return [statusColors[selectedStatus]];
    }

    const keyMap = { "In Progress": "inProgress",  Pending: "pending" ,Closed: "closed", };
    const activeStatuses = ["In Progress","Pending","Closed",].filter((s) =>
      rawData.some((item) => (item[keyMap[s]] || 0) > 0)
    );

    return activeStatuses.map((s) => statusColors[s]);
  }, [rawData, selectedStatus]);

  const getMaxYValue = useCallback(() => {
    if (chartData.length <= 1) return 100;
    const values = chartData.slice(1).flatMap(row => row.slice(1).map(Number));
    const max = Math.max(...values, 0);

    if (max <= 10) return 10;
    if (max <= 50) return Math.ceil(max / 5) * 5;
    return Math.ceil(max / 10) * 10;
  }, [chartData]);

  const totalAssessments =
    chartData.length > 1
      ? chartData
          .slice(1)
          .reduce((sum, row) => sum + row.slice(1).reduce((a, v) => a + Number(v), 0), 0)
      : 0;

const options = {
  backgroundColor: "transparent",
  chartArea: { 
    width: "80%", 
    height: "70%", 
    top: 20, 
    bottom: chartData.length > 4 ? 80 : 60 
  },
  isStacked: false,
  legend: {
    position: totalAssessments === 0 ? "none" : "bottom",
    textStyle: { color: isDark ? "#eaeaea" : "#444", fontSize: 11, fontName: "Poppins" },
  },
  hAxis: {
    gridlines: { color: isDark ? "#444" : "#eaeaea" },
    textStyle: { color: isDark ? "#eaeaea" : "#444", fontSize: 10, fontName: "Poppins" },
    slantedText: chartData.length > 4,
    slantedTextAngle: 65,
    showTextEvery: 1,
    textPosition: "out",
  },
  vAxis: {
    minValue: 0,
    viewWindow: { min: 0 },
    maxValue: getMaxYValue(),
    gridlines: { count: 5, color: isDark ? "#444" : "#eaeaea" },
    textStyle: { color: isDark ? "#eaeaea" : "#444", fontSize: 10, fontName: "Poppins" },
  },
  tooltip: { 
    isHtml: false,
    trigger: "focus",
    textStyle: { fontSize: 10, fontName: "Poppins" }, 
    showColorCode: true 
  },
  colors: getActiveColors(),
  bar: { groupWidth: selectedStatus === "All" ? "90%" : "70%" },
};

  const { width, ref } = useResizeDetector();

  if (loading) {
    return (
      <div className="charts" style={{ height: "357px", padding: "14px", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Skeleton width="140px" height="24px" />
          <Skeleton width="100px" height="24px" />
        </div>
        <Skeleton width="200px" height="16px" className="mt-2" />
        <Skeleton width="100%" height="250px" className="mt-4" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: "357px", padding: 14, color: "red", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        {error}
      </div>
    );
  }

    const getSummaryLabel = (count) => {
  switch (selectedStatus) {
    case "Pending":
      return count === 1 ? "Pending User" : "Pending Users";

    case "In Progress":
      return count === 1 ? "User In Progress" : "Users In Progress";

    case "Closed":
      return count === 1 ? "Completed User" : "Completed Users";

    default:
      return count === 1
        ? "Total Assessment User"
        : "Total Assessment Users";
  }
};

  return (
    <div
      className="charts p-3"
      style={{
        height: "357px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        borderRadius: 12,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
      ref={ref}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 16 }}>Assessment</div>
          <p style={{ fontSize: 13, color: isDark ? "#eaeaea" : "#6c757d", margin: "4px 0 0" }}>
           {getSummaryLabel(totalAssessments)}: <b>{totalAssessments}</b>
          </p>
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          style={{
            fontSize: 12,
            border: "1px solid #ddd",
            borderRadius: 6,
            padding: "6px 10px",
            backgroundColor: isDark ? "#222" : "#fff",
            color: isDark ? "#eaeaea" : "#000",
            cursor: "pointer",
          }}
        >
          <option value="All">All</option>
          <option value="In Progress">In Progress</option>
          <option value="Pending">Pending</option>
          <option value="Closed">Closed</option>
        </select>
      </div>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {totalAssessments === 0 ? (
          <div style={{ color: isDark ? "#999" : "#888", fontSize: 15 }}>
            No assessment data available
          </div>
        ) : width ? (
          <Chart
           key={width}
            chartType="ColumnChart"
            data={chartData}
            options={options}
            width={`${width - 50}px`}
            height="265px"
          />
        ) : null}
      </div>
    </div>
  );
};

export default AssessmentBarChart;
